import { useNavigationContainerRef } from "@-/com.npmjs.expo-router";
import { CommonActions } from "@-/com.npmjs.expo-router/react-navigation";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";
import { useEffect, useRef } from "react";
import { subscribeLearnReset } from "#request-learn-reset";

/**
 * The Stack screen name the Learn navigator mounts its summary route under. `LearnLayout` derives
 * the same value via `path.relative(base, route)`.
 */
const LEARN_SUMMARY_SCREEN_NAME = "summary";

/** Bottom-tab route name for the Learn tab (see `use-primary-navigation-strip-screens.tsx`). */
const LEARN_TAB_NAME = "learn";

/**
 * The Learn Stack's `lesson` child route. Matching on `summary` ALONE is ambiguous enough to avoid
 * relying on it as a global route identity. The Learn Stack is uniquely the one that mounts BOTH
 * `summary` AND `lesson`, so we require both to pick it out unambiguously. Matching `(nav)`
 * instead made the reset target the wrong navigator, leaving the open lesson route in place and
 * the Learn summary unfocused.
 */
const LEARN_LESSON_SCREEN_NAME = "lesson";

type NavState = {
  index?: number;
  key: string;
  routeNames?: string[];
  routes: { key: string; name: string; state?: NavState }[];
};

/**
 * Finds the Learn Stack navigator state — the one whose `routeNames` includes BOTH the `summary`
 * screen and the `lesson` child route. Both are required because only the
 * Learn Stack has `lesson`, so the pair identifies it uniquely wherever the bottom-tab group sits.
 */
function findLearnStackState(state: NavState | undefined): NavState | undefined {
  if (state === undefined) {
    return undefined;
  }
  if (
    state.routeNames?.includes(LEARN_SUMMARY_SCREEN_NAME) === true &&
    state.routeNames.includes(LEARN_LESSON_SCREEN_NAME)
  ) {
    return state;
  }
  for (const route of state.routes) {
    const found = findLearnStackState(route.state as NavState | undefined);
    if (found !== undefined) {
      return found;
    }
  }
  return undefined;
}

/**
 * Walks the active route chain (each navigator's currently-focused route) from the root and reports
 * whether it passes through the Learn bottom tab — i.e. whether Learn is the tab the user is on.
 */
function isLearnTabActive(state: NavState | undefined): boolean {
  let current = state;
  while (current !== undefined) {
    const activeRoute = current.routes[current.index ?? 0];
    if (activeRoute === undefined) {
      return false;
    }
    if (activeRoute.name === LEARN_TAB_NAME) {
      return true;
    }
    current = activeRoute.state as NavState | undefined;
  }
  return false;
}

/**
 * Subscribes to "reset onboarding" and resets the Learn Stack back to its `summary` screen — so the
 * just-restored intro slides surface there — WITHOUT switching the user off the tab they reset
 * from.
 *
 * The reset re-creates summary (summary is `router.replace`d out of the stack when a lesson is
 * entered, so there's nothing to `dismissTo`/`popToTop` back to). Re-creating it makes
 * `/learn/summary` the active route, and summary's own focus redirect would then `router.replace` to the welcome
 * slide — stealing focus onto Learn if performed while the user is on another tab (e.g. Settings).
 *
 * To avoid that, the reset is DEFERRED: it runs immediately only when Learn is already the active
 * tab; otherwise it waits for the user to next open the Learn tab (a root navigation-state listener
 * flushes it then), at which point being on Learn is exactly where the slides should appear.
 */
export function useLearnStackReset(): void {
  const navigationRef = useNavigationContainerRef();
  const resetPendingRef = useRef(false);

  useEffect(() => {
    const flushIfLearnActive = () => {
      if (!resetPendingRef.current) {
        return;
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- react-navigation's NavigationState is a structural superset of the minimal NavState shape this hook traverses
      const rootState = navigationRef.getRootState() as unknown as NavState | undefined;
      if (!isLearnTabActive(rootState)) {
        return;
      }
      const learnStackState = findLearnStackState(rootState);
      if (learnStackState === undefined) {
        return;
      }
      resetPendingRef.current = false;
      const learnStackKey = learnStackState.key;
      // Defer the reset until AFTER the current render/navigation commit settles. When this flush
      // runs from the `state` listener during a bottom-tab switch ONTO Learn, react-navigation is
      // mid-commit of that tab transition; dispatching the stack reset synchronously inside that
      // commit was silently dropped (the open lesson route survived → the summary never focused →
      // no slide redirect). An animation frame fires just before the next paint — i.e. after
      // React has committed — so the reset applies to the settled tree (the RN-idiomatic
      // "after commit" primitive, over the old `setTimeout(…, 0)` macrotask-yield spelling).
      //
      // Guarded (`safeRequestAnimationFrame`): a bare rAF callback is as detached as a bare timer,
      // so a synchronous throw here — e.g. `dispatch` against a navigator key torn down by the
      // concurrent post-sign-in `resetQueries()`/re-suspend burst — would escape to the New-Arch
      // C++ scheduler and `std::terminate` the process (HAN-36) instead of surfacing a red-box.
      safeRequestAnimationFrame(() => {
        navigationRef.dispatch({
          ...CommonActions.reset({
            index: 0,
            routes: [{ name: LEARN_SUMMARY_SCREEN_NAME }],
          }),
          target: learnStackKey,
        });
        navigationRef.dispatch(
          CommonActions.navigate({
            name: LEARN_TAB_NAME,
            params: { screen: LEARN_SUMMARY_SCREEN_NAME },
          }),
        );
      }, "learn-stack-reset");
    };

    const unsubscribeReset = subscribeLearnReset(() => {
      resetPendingRef.current = true;
      // Run now if the user is already on Learn; otherwise the state listener flushes it when they
      // next switch to the Learn tab.
      flushIfLearnActive();
    });
    // `state` fires on every navigation change, including bottom-tab switches.
    const unsubscribeState = navigationRef.addListener("state", flushIfLearnActive);

    return () => {
      unsubscribeReset();
      unsubscribeState();
    };
  }, [navigationRef]);
}
